/**
 * components/viewer/RevisionImpactStationsBanner.jsx — Iter 50.
 *
 * Thin sheet-header strip that orients field users to where on the
 * alignment the active revision touches. Rendered ONLY when:
 *   1. The sheet has ≥1 alignment, AND
 *   2. Calling `/api/sheets/{id}/revision_impact_stations` resolves a
 *      [begin, end] range from real structure positions on this sheet.
 *
 * Otherwise renders `null` so plan sets without stationing stay quiet.
 *
 * Tap the banner → fires a Jump intent to the alignment's begin station
 * so the camera centers on the start of the affected reach.
 */
import React, { useEffect, useState } from "react";
import { Route, ArrowRight } from "lucide-react";
import { fetchRevisionImpactStations } from "@/lib/stationing";
import { dispatchJump } from "@/lib/jumpTo";

export default function RevisionImpactStationsBanner({ sheetId, reviewId }) {
  const [rng, setRng] = useState(null);

  useEffect(() => {
    if (!sheetId) { setRng(null); return; }
    let cancel = false;
    fetchRevisionImpactStations(sheetId).then((r) => {
      if (!cancel) setRng(r);
    });
    return () => { cancel = true; };
  }, [sheetId, reviewId]);

  if (!rng || !rng.resolved) return null;

  return (
    <button
      type="button"
      onClick={() => dispatchJump({
        object_type: "alignment",
        object_id: rng.alignment_id,
        sheet_id: sheetId,
        label: `${rng.begin_label} → ${rng.end_label}`,
      })}
      className="w-full flex items-center gap-2 px-3 py-1.5 border-y border-[var(--fop-border)] bg-[var(--fop-surface-2)]/60 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:bg-[var(--fop-surface-2)] transition-colors"
      data-testid="revision-impact-stations-banner"
      data-alignment-id={rng.alignment_id}
      data-begin-station={rng.begin_station?.toFixed?.(2)}
      data-end-station={rng.end_station?.toFixed?.(2)}
    >
      <Route size={11} className="text-[var(--fop-primary)] shrink-0" />
      <span className="text-[var(--fop-text-3)]">Affects</span>
      <b className="text-[var(--fop-primary)] font-semibold">{rng.begin_label}</b>
      <ArrowRight size={9} className="text-[var(--fop-text-3)]" />
      <b className="text-[var(--fop-primary)] font-semibold">{rng.end_label}</b>
      <span className="text-[var(--fop-text-3)] truncate ml-1">
        on <span className="text-[var(--fop-text-2)]">{rng.alignment_name}</span>
      </span>
      <span className="ml-auto text-[var(--fop-text-4)]">{rng.units}</span>
    </button>
  );
}
